import type { Application, Route } from '../types';

export type BusinessDocumentKey =
  | 'CLIENT_APPLICATION'
  | 'CLIENT_CONTRACT_REQUEST'
  | 'CLIENT_INVOICE'
  | 'CLIENT_UPD'
  | 'CLIENT_PRIMARY_DOCS'
  | 'CLIENT_TN_TTN_PACKAGE'
  | 'CARRIER_CONTRACT_REQUEST'
  | 'CARRIER_INVOICE'
  | 'CARRIER_UPD'
  | 'CARRIER_TN_TTH_ORIGINALS'
  | 'LOADING_DOCUMENTS'
  | 'UNLOADING_DOCUMENTS'
  | 'LOADING_PHOTOS'
  | 'UNLOADING_PHOTOS'
  | 'RAIL_WAYBILL'
  | 'AIR_WAYBILL';

export type BusinessDocumentParty = 'CLIENT' | 'CARRIER';
export type BusinessTransportMode = 'AUTO' | 'RAIL' | 'AIR' | 'ALL';
export type BusinessApplicationBasis = 'CONTRACT' | 'CONTRACT_REQUEST' | 'ALL';
export type BusinessDocumentProcessStage = 'basis' | 'loading' | 'unloading' | 'closing' | 'settlement';
export type BusinessDocumentActivationEvent =
  | 'ALWAYS'
  | 'FIRST_LOADING_COMPLETED'
  | 'UNLOADING_COMPLETED'
  | 'ROUTE_COMPLETED';

export type BusinessDocumentDefinition = {
  key: BusinessDocumentKey;
  label: string;
  catalogCode: string;
  legacyDocumentType: string;
  party: BusinessDocumentParty;
  modes: BusinessTransportMode[];
  basis: BusinessApplicationBasis[];
  stage: 'loading' | 'unloading' | 'other';
  processStage: BusinessDocumentProcessStage;
  activationEvent: BusinessDocumentActivationEvent;
  fileType: 'file' | 'photo';
  required: boolean;
  uploadable: boolean;
};

export const BUSINESS_DOCUMENT_DEFINITIONS: BusinessDocumentDefinition[] = [
  {
    key: 'CLIENT_APPLICATION',
    label: 'Заявка клиента',
    catalogCode: 'DOC-CL-002',
    legacyDocumentType: 'primary_docs',
    party: 'CLIENT',
    modes: ['ALL'],
    basis: ['CONTRACT'],
    stage: 'other',
    processStage: 'basis',
    activationEvent: 'ALWAYS',
    fileType: 'file',
    required: true,
    uploadable: true,
  },
  {
    key: 'CLIENT_CONTRACT_REQUEST',
    label: 'Договор-заявка клиента',
    catalogCode: 'DOC-CL-001',
    legacyDocumentType: 'primary_docs',
    party: 'CLIENT',
    modes: ['ALL'],
    basis: ['CONTRACT_REQUEST'],
    stage: 'other',
    processStage: 'basis',
    activationEvent: 'ALWAYS',
    fileType: 'file',
    required: true,
    uploadable: true,
  },
  {
    key: 'CLIENT_INVOICE',
    label: 'Счёт клиенту',
    catalogCode: 'DOC-CL-003',
    legacyDocumentType: 'INVOICE',
    party: 'CLIENT',
    modes: ['ALL'],
    basis: ['ALL'],
    stage: 'other',
    processStage: 'settlement',
    activationEvent: 'ROUTE_COMPLETED',
    fileType: 'file',
    required: true,
    uploadable: true,
  },
  {
    key: 'CLIENT_UPD',
    label: 'УПД клиенту',
    catalogCode: 'DOC-CL-004',
    legacyDocumentType: 'UPD_ACT',
    party: 'CLIENT',
    modes: ['ALL'],
    basis: ['ALL'],
    stage: 'other',
    processStage: 'settlement',
    activationEvent: 'ROUTE_COMPLETED',
    fileType: 'file',
    required: true,
    uploadable: true,
  },
  {
    key: 'CLIENT_PRIMARY_DOCS',
    label: 'Первичные документы клиента',
    catalogCode: 'DOC-CL-005',
    legacyDocumentType: 'primary_docs',
    party: 'CLIENT',
    modes: ['ALL'],
    basis: ['ALL'],
    stage: 'other',
    processStage: 'loading',
    activationEvent: 'FIRST_LOADING_COMPLETED',
    fileType: 'file',
    required: true,
    uploadable: true,
  },
  {
    key: 'CLIENT_TN_TTN_PACKAGE',
    label: 'Оригиналы ТН/ТТН клиентские отправленные',
    catalogCode: 'DOC-CL-006',
    legacyDocumentType: 'ORIGINAL_TN',
    party: 'CLIENT',
    modes: ['ALL'],
    basis: ['ALL'],
    stage: 'other',
    processStage: 'closing',
    activationEvent: 'ROUTE_COMPLETED',
    fileType: 'file',
    required: true,
    uploadable: true,
  },
  {
    key: 'CARRIER_CONTRACT_REQUEST',
    label: 'Договор-заявка перевозчика',
    catalogCode: 'DOC-CR-001',
    legacyDocumentType: 'CARRIER_CONTRACT_REQUEST',
    party: 'CARRIER',
    modes: ['ALL'],
    basis: ['ALL'],
    stage: 'other',
    processStage: 'basis',
    activationEvent: 'ALWAYS',
    fileType: 'file',
    required: true,
    uploadable: true,
  },
  {
    key: 'CARRIER_INVOICE',
    label: 'Счёт перевозчика',
    catalogCode: 'DOC-CR-002',
    legacyDocumentType: 'CARRIER_INVOICE',
    party: 'CARRIER',
    modes: ['ALL'],
    basis: ['ALL'],
    stage: 'other',
    processStage: 'settlement',
    activationEvent: 'ROUTE_COMPLETED',
    fileType: 'file',
    required: true,
    uploadable: true,
  },
  {
    key: 'CARRIER_UPD',
    label: 'УПД перевозчика',
    catalogCode: 'DOC-CR-003',
    legacyDocumentType: 'UPD_ACT',
    party: 'CARRIER',
    modes: ['ALL'],
    basis: ['ALL'],
    stage: 'other',
    processStage: 'settlement',
    activationEvent: 'ROUTE_COMPLETED',
    fileType: 'file',
    required: true,
    uploadable: true,
  },
  {
    key: 'CARRIER_TN_TTH_ORIGINALS',
    label: 'Оригиналы ТН/ТТН',
    catalogCode: 'DOC-CR-004',
    legacyDocumentType: 'ORIGINAL_TN',
    party: 'CARRIER',
    modes: ['ALL'],
    basis: ['ALL'],
    stage: 'other',
    processStage: 'closing',
    activationEvent: 'ROUTE_COMPLETED',
    fileType: 'file',
    required: true,
    uploadable: true,
  },
  {
    key: 'LOADING_DOCUMENTS',
    label: 'Документы погрузки',
    catalogCode: 'DOC-CR-005',
    legacyDocumentType: 'LOADING_TN',
    party: 'CARRIER',
    modes: ['AUTO'],
    basis: ['ALL'],
    stage: 'loading',
    processStage: 'loading',
    activationEvent: 'FIRST_LOADING_COMPLETED',
    fileType: 'file',
    required: true,
    uploadable: true,
  },
  {
    key: 'UNLOADING_DOCUMENTS',
    label: 'Документы выгрузки',
    catalogCode: 'DOC-CR-006',
    legacyDocumentType: 'UNLOADING_DOC',
    party: 'CARRIER',
    modes: ['ALL'],
    basis: ['ALL'],
    stage: 'unloading',
    processStage: 'unloading',
    activationEvent: 'UNLOADING_COMPLETED',
    fileType: 'file',
    required: true,
    uploadable: true,
  },
  {
    key: 'LOADING_PHOTOS',
    label: 'Фото погрузки',
    catalogCode: 'DOC-CR-007',
    legacyDocumentType: 'LOADING_PHOTO',
    party: 'CARRIER',
    modes: ['AUTO'],
    basis: ['ALL'],
    stage: 'loading',
    processStage: 'loading',
    activationEvent: 'FIRST_LOADING_COMPLETED',
    fileType: 'photo',
    required: true,
    uploadable: true,
  },
  {
    key: 'UNLOADING_PHOTOS',
    label: 'Фото выгрузки',
    catalogCode: 'DOC-CR-008',
    legacyDocumentType: 'UNLOADING_PHOTO',
    party: 'CARRIER',
    modes: ['ALL'],
    basis: ['ALL'],
    stage: 'unloading',
    processStage: 'unloading',
    activationEvent: 'UNLOADING_COMPLETED',
    fileType: 'photo',
    required: true,
    uploadable: true,
  },
  {
    key: 'RAIL_WAYBILL',
    label: 'ЖД накладная',
    catalogCode: 'DOC-TM-001',
    legacyDocumentType: 'RAIL_WAYBILL',
    party: 'CARRIER',
    modes: ['RAIL'],
    basis: ['ALL'],
    stage: 'loading',
    processStage: 'loading',
    activationEvent: 'FIRST_LOADING_COMPLETED',
    fileType: 'file',
    required: true,
    uploadable: true,
  },
  {
    key: 'AIR_WAYBILL',
    label: 'AWB / авиадокументы',
    catalogCode: 'DOC-TM-002',
    legacyDocumentType: 'AWB',
    party: 'CARRIER',
    modes: ['AIR'],
    basis: ['ALL'],
    stage: 'loading',
    processStage: 'loading',
    activationEvent: 'FIRST_LOADING_COMPLETED',
    fileType: 'file',
    required: true,
    uploadable: true,
  },
];

export function normalizeBusinessTransportMode(value?: string | null): BusinessTransportMode {
  const normalized = String(value || '').trim().toUpperCase();
  if (normalized === 'RAIL' || normalized === 'RAILWAY') return 'RAIL';
  if (normalized === 'AIR' || normalized === 'AVIATION') return 'AIR';
  return 'AUTO';
}

export function resolveApplicationBasis(application?: Pick<Application, 'applicationBasis' | 'contractType' | 'contractId'> | null): BusinessApplicationBasis {
  const basis = String(application?.applicationBasis || '').trim().toUpperCase();
  if (basis === 'CLIENT_CONTRACT_REQUEST') return 'CONTRACT_REQUEST';
  if (basis === 'CLIENT_CONTRACT' || application?.contractId != null || application?.contractType === 'CLIENT') return 'CONTRACT';
  return 'CONTRACT';
}

export function listBusinessDocumentDefinitions(params: {
  party?: BusinessDocumentParty;
  mode?: BusinessTransportMode;
  basis?: BusinessApplicationBasis;
  uploadableOnly?: boolean;
} = {}): BusinessDocumentDefinition[] {
  const mode = params.mode ?? 'ALL';
  const basis = params.basis ?? 'ALL';

  return BUSINESS_DOCUMENT_DEFINITIONS.filter((definition) => {
    if (params.party && definition.party !== params.party) return false;
    if (params.uploadableOnly && !definition.uploadable) return false;
    if (!definition.modes.includes('ALL') && !definition.modes.includes(mode)) return false;
    if (!definition.basis.includes('ALL') && !definition.basis.includes(basis)) return false;
    return true;
  });
}

export function getUploadBusinessDocumentDefinitions(params: {
  application?: Application | null;
  route?: Route | null;
}): BusinessDocumentDefinition[] {
  const dedupeByLegacyType = (items: BusinessDocumentDefinition[]) => {
    const byLegacyType = new Map<string, BusinessDocumentDefinition>();
    for (const item of items) {
      const key = item.legacyDocumentType.toUpperCase();
      if (!byLegacyType.has(key)) byLegacyType.set(key, item);
    }
    return Array.from(byLegacyType.values());
  };

  if (params.route) {
    return dedupeByLegacyType(listBusinessDocumentDefinitions({
      party: 'CARRIER',
      mode: normalizeBusinessTransportMode(params.route.transportType),
      uploadableOnly: true,
    }));
  }

  if (params.application) {
    return dedupeByLegacyType(listBusinessDocumentDefinitions({
      party: 'CLIENT',
      basis: resolveApplicationBasis(params.application),
      uploadableOnly: true,
    }));
  }

  return dedupeByLegacyType(listBusinessDocumentDefinitions({ uploadableOnly: true }));
}

function routeDate(route: Route | null | undefined, keys: Array<keyof Route>): string | null {
  for (const key of keys) {
    const value = route?.[key];
    if (value != null && String(value).trim()) return String(value);
  }
  return null;
}

export function routeHasFirstLoadingCompleted(route?: Route | null): boolean {
  if (!route) return false;
  const status = String(route.status || '').trim().toUpperCase();
  return Boolean(
    routeDate(route, ['actualLoadingDate']) ||
      ['IN_TRANSIT', 'UNLOADING', 'COMPLETED', 'DELIVERED', 'CLOSED'].includes(status)
  );
}

export function routeHasUnloadingCompleted(route?: Route | null): boolean {
  if (!route) return false;
  const status = String(route.status || '').trim().toUpperCase();
  return Boolean(
    routeDate(route, ['actualUnloadingDate']) ||
      ['COMPLETED', 'DELIVERED', 'CLOSED'].includes(status)
  );
}

export function routeIsCompleted(route?: Route | null): boolean {
  if (!route) return false;
  const status = String(route.status || '').trim().toUpperCase();
  return Boolean(routeDate(route, ['actualUnloadingDate']) || ['COMPLETED', 'DELIVERED', 'CLOSED'].includes(status));
}

export function isBusinessDocumentDefinitionActive(
  definition: Pick<BusinessDocumentDefinition, 'activationEvent'>,
  context: { route?: Route | null; routes?: Route[] | null }
): boolean {
  const routes = context.route ? [context.route] : context.routes ?? [];
  switch (definition.activationEvent) {
    case 'ALWAYS':
      return true;
    case 'FIRST_LOADING_COMPLETED':
      return routes.some(routeHasFirstLoadingCompleted);
    case 'UNLOADING_COMPLETED':
      return routes.some(routeHasUnloadingCompleted);
    case 'ROUTE_COMPLETED':
      return routes.some(routeIsCompleted);
    default:
      return false;
  }
}

export function businessDocumentLabelByLegacyType(
  legacyDocumentType: string,
  params: { application?: Application | null; route?: Route | null } = {}
): string | null {
  const normalized = legacyDocumentType.trim().toUpperCase();
  const definition = getUploadBusinessDocumentDefinitions(params).find(
    (item) => item.legacyDocumentType.toUpperCase() === normalized
  );
  return definition?.label ?? null;
}
